<script lang="ts">
  import { onMount } from "svelte"
  import type {
    OrganizationFrame,
    UserCalendarIntegrationResponse,
    UserProvider,
  } from "@kontextwerk/control-web-client"
  import { userDisplayName } from "@kontextwerk/control-web-frame/shared/logic/user-presentation"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import { mdiCalendarAccountOutline, mdiGoogle, mdiMicrosoftWindows, mdiRefresh } from "@mdi/js"
  import { getAdminApi } from "../../../../../../../../../../api/kehrblatt/client"
  import { filterSelectableOrganizationUsers } from "../../../../../../../../_shared/_logic/organization-users"
  import EmployeeCalendarIntegrationActionBox, {
    type EmployeeCalendarIntegrationRow,
  } from "./_modules/employee-calendar-integration-action-box"

  const {
    organization,
    allowedEmployeeIds = null,
    actorUserId,
    supportDelegationActive,
  } = $props<{
    organization: OrganizationFrame | null
    /** Memberships the confirmed `core.calendar_integrations` overview scope may list. */
    allowedEmployeeIds?: string[] | null
    /** Actual signed-in user; support never replaces it with the represented member. */
    actorUserId: string | null
    supportDelegationActive: boolean
  }>()
  const users = $derived(
    filterSelectableOrganizationUsers<OrganizationFrame["users"][number]>(
      organization?.users ?? [],
      allowedEmployeeIds,
    ),
  )
  let overviews = $state<Record<string, UserCalendarIntegrationResponse>>({})
  let loading = $state(true)
  let loadError = $state(false)

  const setupPath = (
    userId: string,
    provider: UserProvider | null,
    step: string | null,
  ): string | null => {
    const segment = organization?.slug ?? organization?.id

    if (!segment) return null

    const path = `/organizations/${encodeURIComponent(segment)}/users/${encodeURIComponent(userId)}/integrations`
    const params = new URLSearchParams()

    if (provider) params.set("calendar_provider", provider)

    if (step) params.set("calendar_setup_step", step)

    return `${path}${params.size ? `?${params}` : ""}#calendar-integration`
  }
  const rows = $derived<EmployeeCalendarIntegrationRow[]>(
    users.map((user) => {
      // Calendar integrations are user-keyed; the overview scope is the membership (`id`).
      const personId = user.user_id ?? ""
      const overview = overviews[personId]
      const active =
        overview?.providers.find((provider) => provider.calendar_capability_active) ?? null
      const write = active?.calendars.find((calendar) => calendar.projection_enabled) ?? null
      const reads = active?.calendars.filter((calendar) => calendar.import_enabled) ?? []
      const complete = Boolean(write && reads.length)
      // Setup and sync are personal: only the signed-in user's own row, never through support.
      const personal = !supportDelegationActive && Boolean(actorUserId) && personId === actorUserId
      const status = !active
        ? "Nicht verbunden"
        : active.account_status === "disconnecting"
          ? "Wird getrennt"
          : active.needs_reauthorization
            ? "Anmeldung erforderlich"
            : complete
              ? "Eingerichtet"
              : "Einrichtung offen"
      const statusTone: EmployeeCalendarIntegrationRow["statusTone"] = !active
        ? "neutral"
        : active.needs_reauthorization || active.account_status === "error"
          ? "error"
          : complete
            ? "success"
            : "warning"
      const step = !active
        ? "connect_account"
        : active.needs_reauthorization
          ? "connect_account"
          : !write
            ? "select_write_target"
            : !reads.length
              ? "select_read_sources"
              : null

      return {
        employeeId: personId,
        employeeLabel: userDisplayName(user) || "Unbekannt",
        provider: active?.provider ?? null,
        providerLabel:
          active?.provider === "google"
            ? "Google"
            : active?.provider === "microsoft"
              ? "Microsoft"
              : "Noch nicht ausgewählt",
        providerIcon:
          active?.provider === "google"
            ? mdiGoogle
            : active?.provider === "microsoft"
              ? mdiMicrosoftWindows
              : mdiCalendarAccountOutline,
        accountEmail: active?.account_email ?? null,
        statusLabel: status,
        statusTone,
        statusColor: complete
          ? "green"
          : active?.needs_reauthorization
            ? "red"
            : active
              ? "orange"
              : "blue",
        statusTooltip: "",
        description: !active
          ? "Für diesen Mitarbeiter ist noch keine Kalenderverbindung eingerichtet."
          : active.account_status === "disconnecting"
            ? "Die Kalenderverbindung wird im Hintergrund getrennt."
            : active.needs_reauthorization
              ? "Die Verbindung benötigt eine erneute Anmeldung."
              : complete
                ? "Kalenderverbindung und Ziele sind vollständig eingerichtet."
                : "Die Verbindung besteht; Kalenderziele müssen noch eingerichtet werden.",
        targetCalendarLabel: write?.display_name || "Noch nicht ausgewählt",
        readSourcesLabel:
          reads
            .map((calendar) => calendar.display_name || calendar.provider_calendar_id)
            .join(", ") || "Noch nicht ausgewählt",
        lastSyncLabel: active?.last_successful_import_at
          ? new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeStyle: "short" }).format(
              new Date(active.last_successful_import_at),
            )
          : "Noch kein erfolgreicher Abruf",
        showConnectionDetails: Boolean(active && active.account_status !== "disconnecting"),
        actionLabel: !active
          ? "Verbinden"
          : complete
            ? "Verwalten"
            : active.needs_reauthorization
              ? "Neu verbinden"
              : "Einrichtung fortsetzen",
        actionUrl:
          personal && active?.account_status !== "disconnecting"
            ? setupPath(personId, active?.provider ?? null, step)
            : null,
        canSync: Boolean(personal && complete && active?.account_status === "connected"),
      }
    }),
  )
  const load = async (): Promise<void> => {
    if (!organization?.id || !users.length) {
      loading = false

      return
    }

    loading = true
    loadError = false
    try {
      const response = await getAdminApi().calendars.getUserIntegrationsBatch(organization.id, {
        user_ids: users.map((user) => user.user_id).filter((id): id is string => Boolean(id)),
      })
      overviews = Object.fromEntries(response.items.map((item) => [item.user_id, item]))
    } catch {
      loadError = true
    } finally {
      loading = false
    }
  }
  const sync = async (row: EmployeeCalendarIntegrationRow): Promise<void> => {
    const active = overviews[row.employeeId]?.providers.find(
      (provider) => provider.calendar_capability_active,
    )

    if (!organization?.id || !active?.provider_account_id) return

    await getAdminApi().calendars.requestSync(organization.id, row.employeeId, active.provider, {
      provider_account_id: active.provider_account_id,
      force_full: true,
    })
  }
  onMount(() => {
    void load()
  })
</script>

<div class="calendar-integration-management form light">
  <p class="intro"
    >Hier sehen Sie den Stand der Kalenderintegrationen. Kalenderprovider, Schreibziel und die
    Kalender für belegte Zeiten richtet jeder Mitarbeiter für sein eigenes Konto ein.</p
  >
  {#if supportDelegationActive}<p role="note"
      >Während einer Support-Vertretung sind Kalenderintegrationen nur lesbar.</p
    >{/if}
  {#if loading}<p role="status">Kalenderintegrationen werden geladen …</p>
  {:else if loadError}<div class="error" role="alert"
      ><span>Kalenderintegrationen konnten nicht geladen werden.</span><CTA
        type="secondaryButton"
        icon={mdiRefresh}
        content="Erneut laden"
        onClick={load}
      /></div
    >
  {:else if rows.length}
    {#each rows as row (row.employeeId)}<EmployeeCalendarIntegrationActionBox
        {row}
        onSync={sync}
      />{/each}
  {:else}<p>Für diesen Kalender sind keine Mitarbeiter verfügbar.</p>{/if}
</div>

<style lang="less">
  .calendar-integration-management {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    color: var(--text-100);
  }
  .intro,
  p {
    margin: 0;
    line-height: 1.45;
  }
  .intro {
    opacity: 0.78;
  }
  .error {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
  }
</style>
